<template>
  <div class="organizations">
    <div class="selector-section">
      <div class="title">Organizations</div>
      <ButtonMenu
        ref="menuEl"
        :offset="[-8, -4]"
        placement="bottom-start"
        class="organization-selector"
      >
        <template #btn>
          <BaseButton
            v-if="selectedOrganization"
            class="selected-org"
            kind="auxiliary"
            type="secondary"
            :text="selectedOrganization.name"
            :is-expanded="true"
          >
            <template #right>
              <span class="org-selector-trigger-right">
                <span v-if="selectedOrganization.personal" class="default-tag"
                  >Private</span
                >
                <span aria-hidden="true">
                  <ChevronDownIcon class="org-selector-chevron" />
                </span>
              </span>
            </template>
          </BaseButton>
          <BaseButton
            v-else
            class="select-org"
            kind="auxiliary"
            type="secondary"
            text="Select Organization"
            :is-expanded="true"
          >
            <template #right>
              <span aria-hidden="true">
                <ChevronDownIcon class="org-selector-chevron" />
              </span>
            </template>
          </BaseButton>
        </template>
        <template #content>
          <div class="organization-options">
            <template v-for="orgId in sortedOrganizations" :key="orgId">
              <BaseButton
                kind="auxiliary"
                type="tertiary"
                :text="organizationDetails?.[orgId]?.name || 'Loading...'"
                :state="
                  selectedOrganization?.id === orgId ? 'active' : 'default'
                "
                :is-expanded="true"
                @click="selectOrganization(orgId)"
              >
                <template v-if="organizationDetails?.[orgId]?.personal" #right>
                  <span class="default-tag">Private</span>
                </template>
              </BaseButton>
            </template>
            <UpgradeButton
              class="create-organization"
              :entitlement-key="ENTITLEMENT_KEY"
              kind="auxiliary"
              type="primary"
              text="New"
              :is-expanded="true"
              @pre-click="handleUnentitledOrganizationPreClick"
              @click="handleCreateOrganization"
            >
              <template #icon>
                <AddIcon />
              </template>
            </UpgradeButton>
          </div>
        </template>
      </ButtonMenu>
    </div>

    <template v-if="selectedOrganization">
      <Organization :organization="selectedOrganization" />
    </template>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, ref } from "vue";
import { useModal } from "vue-final-modal";
import { useRoute } from "vue-router";

import { FullOrganization } from "@ctrl/controller-rpc";

import BaseButton from "@/components/base/BaseButton.vue";
import UpgradeButton from "@/components/base/UpgradeButton.vue";
import ButtonMenu from "@/components/elements/ButtonMenu.vue";
import AddIcon from "@/components/icons/AddIcon.vue";
import ChevronDownIcon from "@/components/icons/ChevronDownIcon.vue";
import * as inject from "@/components/inject";
import CreateOrganizationModal from "@/components/modals/CreateOrganizationModal.vue";
import Organization from "@/components/settings/Organization.vue";
import { getLastAccessedOrganization } from "@/composables/useOrganization";
import { bindAsync, releasableRef, useReleaser } from "@/ref";
import { RouteNames } from "@/routes/types";
import { navigateTo } from "@/utils/navigate";

type OrganizationMap = Record<string, FullOrganization>;

const ENTITLEMENT_KEY = "organizations";

export default defineComponent({
  name: "Organizations",
  components: {
    BaseButton,
    UpgradeButton,
    ButtonMenu,
    Organization,
    AddIcon,
    ChevronDownIcon,
  },
  async setup() {
    const controller = inject.controller();
    const router = inject.router();
    const route = useRoute();
    const profile = inject.profile();
    const billing = inject.billing();
    const menuEl = ref<InstanceType<typeof ButtonMenu>>();

    const personalOrganizationId = profile.value?.user.personal.organizationId;

    // billing is async-initialized; keep entitlement reactive as it becomes available
    const organizationsEntitlementRef = computed(() => {
      if (!personalOrganizationId) return undefined;
      return billing.value?.isEntitledTo(
        ENTITLEMENT_KEY,
        personalOrganizationId
      );
    });
    const isEntitledToOrganizations = computed(() => {
      return organizationsEntitlementRef.value?.value ?? false;
    });

    // Load organizations and their details reactively using bindAsync pattern
    const releaser = useReleaser();
    const [organizations, organizationDetails] = await (async () => {
      const organizations = releaser.add(controller.organization.all());

      const organizationDetails = releaser.add(
        organizations.then((ids) =>
          bindAsync(ids, async (ids) => {
            if (!ids?.length) return releasableRef<OrganizationMap>({});

            const orgPromises = ids.map(async (id: string) => {
              const orgRef = await controller.organization.get(id);
              const org = orgRef.value;
              orgRef.release();
              if (!org) return null;
              return { id, org };
            });

            const loadedOrgs = await Promise.all(orgPromises);
            const details: OrganizationMap = {};
            loadedOrgs.forEach((result) => {
              if (result) {
                details[result.id] = {
                  ...result.org,
                };
              }
            });
            return releasableRef(details);
          })
        )
      );

      return Promise.all([organizations, organizationDetails]);
    })();

    // Derive the selected organization from the route, then the persisted
    // last-accessed organization, then the personal one, then the first one.
    const selectedOrganization = computed((): FullOrganization | null => {
      const routeOrgParam = route.params.orgId;
      const routeOrgId = Array.isArray(routeOrgParam)
        ? routeOrgParam[0]
        : routeOrgParam;

      const candidates = [
        routeOrgId,
        getLastAccessedOrganization(profile.value),
        profile.value.user.personal.organizationId,
        organizations.value[0],
      ];
      for (const orgId of candidates) {
        if (orgId && organizations.value.includes(orgId)) {
          return organizationDetails?.value?.[orgId] || null;
        }
      }
      return null;
    });

    const sortedOrganizations = computed(() => {
      if (!organizations.value?.length || !organizationDetails.value) {
        return organizations.value || [];
      }

      return [...organizations.value].sort((a, b) => {
        const orgA = organizationDetails.value?.[a];
        const orgB = organizationDetails.value?.[b];

        if (!orgA || !orgB) return 0;

        // Personal organization comes first
        if (orgA.personal && !orgB.personal) return -1;
        if (!orgA.personal && orgB.personal) return 1;

        // If both are personal or both are not personal, sort alphabetically
        return orgA.name.localeCompare(orgB.name);
      });
    });

    // Note: the route is the source of truth for the selected organization;
    // Routine.vue records route orgId params as the last accessed organization.
    const selectOrganization = (orgId: string) => {
      navigateTo(RouteNames.SettingsOrganization, router, {
        params: { orgId },
      });
    };

    const handleUnentitledOrganizationPreClick = () => {
      // Close the dropdown menu first before UpgradeButton opens the modal
      menuEl.value?.hide();
    };

    const handleCreateOrganization = () => {
      const { open } = useModal({
        component: CreateOrganizationModal,
      });
      void open();
    };

    return {
      ENTITLEMENT_KEY,
      organizations,
      organizationDetails,
      sortedOrganizations,
      selectedOrganization,
      selectOrganization,
      handleCreateOrganization,
      personalOrganizationId,
      isEntitledToOrganizations,
      handleUnentitledOrganizationPreClick,
      menuEl,
    };
  },
});
</script>

<style scoped lang="scss">
.organizations {
  display: flex;
  flex-direction: column;
}

.selector-section {
  display: flex;
  flex-direction: column;
  row-gap: 8px;
  padding: 12px 12px 0 12px;

  .title {
    margin-bottom: 4px;
    padding-inline-start: 4px;
    @extend %font-h5;

    @include themed() {
      color: t($grey-50);
    }
  }

  .organization-selector {
    width: 100%;

    .default-tag {
      @extend %font-small-medium;
      @include themed() {
        color: t($grey-40);
      }
    }

    .org-selector-trigger-right {
      display: inline-flex;
      align-items: center;
      gap: 6px;
    }

    .org-selector-chevron {
      flex-shrink: 0;
      display: block;
      width: 7px;
      height: 7px;
    }

    .selected-organization {
      width: 100%;
      display: flex;
      align-items: center;
    }

    .selected-org.base-button.auxiliary.text-only {
      padding: 6px 12px;

      @include themed() {
        border: 1px solid t($grey-15);
      }

      :deep(.left) {
        flex: 1;
      }

      :deep(.right) {
        flex-shrink: 0;
      }
    }
  }
}

.organization-options {
  display: flex;
  flex-direction: column;
  row-gap: 2px;
  width: 235px;

  .default-tag {
    @extend %font-small-medium;
    @include themed() {
      color: t($grey-40);
    }
  }

  .create-organization.none.auxiliary.default {
    @include themed() {
      color: t($grey-60);
    }
  }

  :deep(.base-button.active) {
    @extend %font-caption-medium;
    @include themed() {
      color: t($primary);
    }
  }
}
</style>
